/** Cruscotto Red Flag per direzione ed ExCo: KPI, severità, tipi, temi ricorrenti, fornitori, andamento. */
import { Button, Card, Col, Row, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ANCORA } from "@qa/shared";
import type { CruscottoDto, Severita } from "@qa/shared";
import { api } from "../../api/client";
import { fmtK, fmtNum } from "../../lib/format";
import { usePuo } from "../../lib/permessi";
import { palette } from "../../theme";
import { Kpi, KpiRow, Miss, PageHead, Prop, Stato, Vuoto } from "../../components/ui";

const COLORE_SEV: Record<Severita, string> = { CRITICAL: palette.crit, HIGH: palette.bad, MEDIUM: palette.warn, LOW: palette.secX };

export default function CruscottoPage() {
  const { t } = useTranslation();
  const puo = usePuo();
  const q = useQuery({ queryKey: ["cruscotto"], queryFn: () => api.get<CruscottoDto>("/redflag/cruscotto") });
  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoRedflag") }]}
        title={t("redflag.cruscotto.titolo")}
        sub={t("redflag.cruscotto.sottotitolo")}
        extra={
          <>
            {puo("rf.crea") && (
              <Link to="/redflag/segnalazioni/nuova" data-tip={ANCORA.nuova}>
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("redflag.cruscotto.nuova")}
                </Button>
              </Link>
            )}
            {puo("report.vedi") && (
              <Link to="/redflag/report">
                <Button>{t("redflag.cruscotto.report")}</Button>
              </Link>
            )}
          </>
        }
      />
      <Stato q={q}>
        {(c) => {
          const totSev = c.perSeverita.reduce((a, x) => a + x.n, 0);
          const maxTipo = Math.max(1, ...c.perTipo.map((x) => x.n + x.proposti));
          const maxForn = Math.max(1, ...c.fornitori.map((x) => x.nc + (x.claim ?? 0)));
          const maxAnd = Math.max(1, ...c.andamento.map((x) => x.aperti));
          const delta = c.kpi.criticiDelta;
          return (
            <>
              <KpiRow>
                <Kpi label={t("redflag.cruscotto.aperti")} value={fmtNum(c.kpi.aperti)} note={t("redflag.cruscotto.urgenti", { n: c.kpi.urgenti })} />
                <Kpi
                  label={t("redflag.cruscotto.critici")}
                  value={fmtNum(c.kpi.critici)}
                  color={palette.crit}
                  note={delta === null ? undefined : delta === 0 ? t("redflag.cruscotto.criticiUguali") : t("redflag.cruscotto.criticiDelta", { d: delta > 0 ? `+${delta}` : `${delta}` })}
                />
                <Kpi sensibile label={t("redflag.cruscotto.valore")} value={fmtK(c.kpi.valoreARischio)} note={t("redflag.cruscotto.senzaValore", { n: c.kpi.senzaValore })} />
                <Link to="/redflag/segnalazioni?inRitardo=1" style={{ color: "inherit" }}>
                  <Kpi label={t("redflag.cruscotto.inRitardo")} value={fmtNum(c.kpi.inRitardo)} color={c.kpi.inRitardo ? palette.bad : undefined} note={t("redflag.cruscotto.inRitardoNota")} />
                </Link>
                <Kpi label={t("redflag.cruscotto.azioni")} value={fmtNum(c.kpi.azioniAperte)} note={c.kpi.azioniScadute ? <Miss>{t("redflag.cruscotto.azioniScadute", { n: c.kpi.azioniScadute })}</Miss> : undefined} />
                <Kpi sensibile label={t("redflag.cruscotto.persi")} value={fmtNum(c.kpi.persi)} note={t("redflag.cruscotto.valorePersi", { v: fmtK(c.kpi.valorePersi) })} />
                {puo("costi.vedi") && (
                <Kpi
                  sensibile
                  label={t("redflag.cruscotto.costo")}
                  value={c.kpi.costoNonQualita ? fmtK(c.kpi.costoNonQualita) : "—"}
                  note={c.kpi.costoNonQualita ? undefined : <Miss>{t("redflag.cruscotto.costoVuoto")}</Miss>}
                />
                )}
              </KpiRow>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                  <Card size="small" title={t("redflag.cruscotto.perSeverita")} style={{ height: "100%" }}>
                    <div className="hbar" style={{ margin: "4px 0 8px" }}>
                      {c.perSeverita
                        .filter((x) => x.n > 0)
                        .map((x) => (
                          <i key={x.severita} style={{ width: `${(x.n / Math.max(1, totSev)) * 100}%`, background: COLORE_SEV[x.severita] }}>
                            {x.n}
                          </i>
                        ))}
                    </div>
                    <div className="legend">
                      {c.perSeverita.map((x) => (
                        <span key={x.severita}>
                          <i style={{ background: COLORE_SEV[x.severita] }} />
                          {x.severita}
                        </span>
                      ))}
                    </div>
                    <h3 style={{ marginTop: 18, fontSize: 14 }}>
                      {t("redflag.cruscotto.perTipo")} {c.perTipo.some((x) => x.proposti > 0) && <Prop>{t("redflag.cruscotto.tipoProposto")}</Prop>}
                    </h3>
                    <div className="rank" style={{ marginTop: 10 }}>
                      {c.perTipo.map((x) => (
                        <div className="r" key={x.tipo ?? "-"}>
                          <span>{x.tipo ? <Link to={`/redflag/segnalazioni?tipoSegnalazione=${encodeURIComponent(x.tipo)}&aperte=1`}>{x.tipo}</Link> : <Miss>{t("redflag.cruscotto.tipoDaScegliere")}</Miss>}</span>
                          <div className="bar" style={{ display: "flex" }}>
                            <i style={{ width: `${(x.n / maxTipo) * 100}%`, background: palette.primary }} />
                            <i style={{ width: `${(x.proposti / maxTipo) * 100}%`, background: palette.warnSoft, borderRight: x.proposti ? `1px dashed ${palette.warn}` : undefined }} />
                          </div>
                          <span className="num">
                            {fmtNum(x.n)}
                            {x.proposti > 0 && <span className="muted small"> · {t("redflag.cruscotto.proposti", { n: x.proposti })}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card size="small" title={t("redflag.cruscotto.temi")} style={{ height: "100%" }}>
                    {c.temi.length ? (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                          {c.temi.map((x) => (
                            <tr key={x.causa} style={{ borderBottom: `1px solid ${palette.hair}` }}>
                              <td style={{ padding: "8px 0" }}>
                                <b>{x.causa}</b>
                                <br />
                                <small className="muted">{x.clienti.join(", ")}</small>
                              </td>
                              <td className="num" style={{ textAlign: "right" }}>
                                {x.n}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <Vuoto titolo={t("redflag.cruscotto.temiVuotoTitolo")} testo={t("redflag.cruscotto.temiVuoto")} />
                    )}
                    <p className="muted small" style={{ margin: "8px 0 0" }}>
                      {t("redflag.cruscotto.temiNota")}
                    </p>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card size="small" title={t("redflag.cruscotto.fornitori")} style={{ height: "100%" }}>
                    {c.fornitori.length ? (
                      <div className="rank">
                        {c.fornitori.map((x) => (
                          <div className="r" key={x.fornitore?.id ?? x.nome}>
                            {x.fornitore && puo("fornitore.vedi") ? <Link to={`/fornitori/${x.fornitore.id}`}>{x.nome}</Link> : <span>{x.nome}</span>}
                            <div className="bar">
                              <i style={{ width: `${((x.nc + (x.claim ?? 0)) / maxForn) * 100}%`, background: palette.warn }} />
                            </div>
                            <span className="num">{x.claim === null ? t("redflag.cruscotto.fornitoriRigaNc", { nc: x.nc }) : t("redflag.cruscotto.fornitoriRiga", { nc: x.nc, claim: x.claim })}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Vuoto titolo={t("redflag.cruscotto.fornitoriVuoto")} />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card size="small" title={t("redflag.cruscotto.andamento")} style={{ height: "100%" }}>
                    <div className="chart">
                      {c.andamento.map((x) => (
                        <Tooltip key={x.settimana} title={t("redflag.cruscotto.andamentoTip", { s: x.settimana, n: x.aperti })}>
                          <i style={{ height: `${(x.aperti / maxAnd) * 100}%` }} />
                        </Tooltip>
                      ))}
                    </div>
                    {c.andamento.length > 0 && (
                      <div className="legend">
                        <span className="mono">{c.andamento[0].settimana}</span>
                        <span className="mono" style={{ marginLeft: "auto" }}>
                          {c.andamento[c.andamento.length - 1].settimana}
                        </span>
                      </div>
                    )}
                    <p className="muted small">{t("redflag.cruscotto.andamentoNota")}</p>
                  </Card>
                </Col>
              </Row>
            </>
          );
        }}
      </Stato>
    </>
  );
}
